import React from 'react';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import DeveloperBoardIcon from '@material-ui/icons/DeveloperBoard';
import DevicesOtherIcon from '@material-ui/icons/DevicesOther';
import DialpadIcon from '@material-ui/icons/Dialpad';
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import OpacityIcon from '@material-ui/icons/Opacity';
import PowerIcon from '@material-ui/icons/Power';
import SecurityIcon from '@material-ui/icons/Security';
import SettingsInputAntennaIcon from '@material-ui/icons/SettingsInputAntenna';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import VideocamIcon from '@material-ui/icons/Videocam';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import WarningIcon from '@material-ui/icons/Warning';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import WifiIcon from '@material-ui/icons/Wifi';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import useStyles from './styles';
import { HardwareStatusVariant } from '../../types/HardwareStatus';

export type DeviceStatusIcon =
  | 'system' | 'device' | 'lock' | 'unlock' | 'garage' | 'thermostat'
  | 'contact' | 'motion' | 'smoke' | 'environment' | 'water' | 'camera'
  | 'light' | 'glass_break' | 'panic' | 'control' | 'siren' | 'doorbell'
  | 'repeater' | 'gateway' | 'power' | 'location' | 'medical' | 'audio'
  | 'safe' | 'access' | 'module' | 'unknown';

type Props = {
  label: string;
  variant?: HardwareStatusVariant;
  icon?: DeviceStatusIcon;
};

export const deviceStatusVariantStyles: {
  [key in HardwareStatusVariant]: {
    backgroundColor: string;
    borderColor: string;
    color: string;
  };
} = {
  success: { backgroundColor: '#EAF8F1', borderColor: '#B7E4CE', color: '#037A56' },
  warning: { backgroundColor: '#FFF7E6', borderColor: '#F4CF87', color: '#A35D00' },
  danger: { backgroundColor: '#FDECEC', borderColor: '#F4B8B8', color: '#C62828' },
  neutral: { backgroundColor: '#F3F4F6', borderColor: '#D1D5DB', color: '#4B5563' },
  info: { backgroundColor: '#EAF2FF', borderColor: '#BFD4F8', color: '#2558A8' },
};

const DEVICE_STATUS_ICON_COMPONENTS: {
  [key in DeviceStatusIcon]: React.ElementType;
} = {
  system: SecurityIcon,
  device: DevicesOtherIcon,
  lock: LockIcon,
  unlock: LockOpenIcon,
  garage: MeetingRoomIcon,
  thermostat: AcUnitIcon,
  contact: MeetingRoomIcon,
  motion: DirectionsRunIcon,
  smoke: WhatshotIcon,
  environment: WarningIcon,
  water: OpacityIcon,
  camera: VideocamIcon,
  light: EmojiObjectsIcon,
  glass_break: BrokenImageIcon,
  panic: TouchAppIcon,
  control: DialpadIcon,
  siren: VolumeUpIcon,
  doorbell: NotificationsActiveIcon,
  repeater: SettingsInputAntennaIcon,
  gateway: WifiIcon,
  power: PowerIcon,
  location: LocationOnIcon,
  medical: LocalHospitalIcon,
  audio: VolumeUpIcon,
  safe: LockIcon,
  access: VpnKeyIcon,
  module: DeveloperBoardIcon,
  unknown: HelpOutlineIcon,
};

type DeviceStatusIconViewProps = {
  icon: DeviceStatusIcon;
  className: string;
};

export const DeviceStatusIconView: React.FC<DeviceStatusIconViewProps> = ({
  icon,
  className,
}: DeviceStatusIconViewProps) => {
  const IconComponent = DEVICE_STATUS_ICON_COMPONENTS[icon];
  return <IconComponent className={className} />;
};

const DeviceStatusBadge: React.FC<Props> = ({
  label,
  variant = 'neutral',
  icon = 'unknown',
}: Props) => {
  const classes = useStyles();

  return (
    <span
      className={classes.badge}
      style={deviceStatusVariantStyles[variant]}
      title={label}
    >
      <DeviceStatusIconView icon={icon} className={classes.icon} />
      {label}
    </span>
  );
};

export default React.memo(DeviceStatusBadge);
